import React, { useState, useRef, useEffect } from 'react';
import customCodeService from '../services/customCodeService';

// Claude assistant embedded in the Custom Code editor. Modelled on TemplateAssistantChat, with one
// deliberate difference: it does NOT auto-apply what Claude returns.
//
// The template assistant overwrites the header/footer field on every reply, which is fine for markup
// you can eyeball. Here the field holds code that runs against live reservations, and the author is
// usually mid-edit — silently replacing their work on each turn would lose it. Every code block gets an
// explicit Insert button instead, so applying a suggestion is a decision.

// Split assistant text into ordered prose / ```js code segments.
function parseSegments(text) {
    const segments = [];
    const re = /```(?:js|javascript)?\s*\n([\s\S]*?)```/gi;
    let last = 0;
    let m;
    while ((m = re.exec(text)) !== null) {
        if (m.index > last) segments.push({ type: 'text', value: text.slice(last, m.index) });
        segments.push({ type: 'code', value: m[1].replace(/\s+$/, '') });
        last = m.index + m[0].length;
    }
    if (last < text.length) segments.push({ type: 'text', value: text.slice(last) });
    return segments;
}

const SUGGESTIONS = [
    'Explain what this snippet currently does',
    'Remove the Company profile and put its name in UDFC31',
    'Why might this throw on some records?',
];

// snippetContext: { code, scopeType, chainName, hotelName, samplePayload } — what the assistant needs
// to answer about THIS snippet rather than in the abstract.
// ONE ASSISTANT FOR EVERY KIND OF SNIPPET. `assist` is the call that relays the conversation — sync template
// snippets by default, an integration route's when that screen passes its own — and `suggestions` / `note`
// say what this snippet is about. The chat, the Insert buttons and the "never auto-apply" rule stay one
// implementation.
const CustomCodeAssistant = ({
    snippetContext,
    onInsert,
    assist = customCodeService.assist,
    suggestions = SUGGESTIONS,
    note = 'knows the snippet contract, the pipeline position and your current code',
    // THE SAME CHAT WHERE THERE IS NO EDITOR (Pipelines > Logs, Ask AI). Without onInsert a code block is
    // shown as an example with no Insert button; the words around the chat say what it is for.
    title = 'Code Assistant',
    emptyText = 'Ask for a change and paste it in, or ask what the current snippet does.',
    placeholder = 'Describe the change you want… (Enter to send, Shift+Enter for a new line)',
    // Asked as soon as the chat opens — for a button whose whole meaning is one question.
    initialQuestion = null,
}) => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, loading]);

    // The opening question, asked once. A ref, not state, so a re-render never asks it twice.
    const askedRef = useRef(false);

    const send = async (text) => {
        const t = (text || input).trim();
        if (!t || loading) return;
        const next = [...messages, { role: 'user', content: t }];
        setMessages(next);
        setInput('');
        setError(null);
        setLoading(true);
        try {
            // The CURRENT editor contents go up every turn, not a snapshot from when the chat opened —
            // otherwise the assistant answers about code the user has already changed.
            const res = await assist(next, snippetContext);
            setMessages([...next, { role: 'assistant', content: res?.reply || '(no response)' }]);
        } catch (err) {
            setError(err.response?.data?.error?.message || err.response?.data?.message
                || err.message || 'The assistant request failed.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (initialQuestion && !askedRef.current) { askedRef.current = true; send(initialQuestion); }
    }, [initialQuestion]);

    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
    };

    return (
        <div className="cca">
            <div className="cca-head">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                {title}
                <span className="cca-head-note">{note}</span>
            </div>

            <div className="cca-scroll" ref={scrollRef}>
                {messages.length === 0 && !loading && (
                    <div className="cca-empty">
                        <p>{emptyText}</p>
                        <div className="cca-suggestions">
                            {suggestions.map(s => (
                                <button key={s} type="button" className="cca-suggestion" onClick={() => send(s)}>{s}</button>
                            ))}
                        </div>
                    </div>
                )}

                {messages.map((m, i) => (
                    <div key={i} className={`cca-msg cca-msg-${m.role}`}>
                        {m.role === 'user'
                            ? <div className="cca-bubble">{m.content}</div>
                            : parseSegments(m.content).map((seg, j) => (
                                seg.type === 'code' ? (
                                    <div key={j} className="cca-code">
                                        <div className="cca-code-bar">
                                            <span>{onInsert ? 'suggested snippet' : 'example'}</span>
                                            {onInsert && (
                                                <button type="button" className="btn btn-sm btn-secondary" onClick={() => onInsert(seg.value)}>
                                                    Insert into editor
                                                </button>
                                            )}
                                        </div>
                                        <pre>{seg.value}</pre>
                                    </div>
                                ) : (
                                    seg.value.trim() ? <div key={j} className="cca-bubble">{seg.value.trim()}</div> : null
                                )
                            ))}
                    </div>
                ))}

                {loading && <div className="cca-msg cca-msg-assistant"><div className="cca-bubble cca-thinking">Thinking…</div></div>}
                {error && <div className="cca-error">{error}</div>}
            </div>

            <div className="cca-input">
                <textarea
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                    rows={2}
                    placeholder={placeholder}
                    disabled={loading}
                />
                <button type="button" className="btn btn-primary btn-sm" onClick={() => send()} disabled={loading || !input.trim()}>
                    Send
                </button>
            </div>
        </div>
    );
};

export default CustomCodeAssistant;
